import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import FileUpload from "../Forms/FileUpload";
import FileDisplay from "../Forms/FileDisplay";
import ViewFile from "../Helpers/ViewFile";

/**
 * FileSelectionModal - A reusable modal for selecting and attaching files
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.context - Context for file operations { type: 'user_tpn'|'booking', id: string, booking_id?: string }
 * @param {Function} props.onAttach - Callback when files are attached
 */
export default function FileSelectionModal({
  isOpen,
  onClose,
  context,
  onAttach,
}) {
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter files based on search term
  const filteredFiles = (Array.isArray(files) ? files : [])
    .filter((file) => file && typeof file === "object")
    .filter((file) =>
      (file.name || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Reset state when modal opens/closes
  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const bookingId = context.current_booking?.id ?? 0;
      const response = await axios.get(`/booking/${bookingId}/files`);

      // Handle new response format with files property
      if (response.data?.files && Array.isArray(response.data.files)) {
        setFiles(response.data.files);
      } else if (Array.isArray(response.data)) {
        // Fallback for old format (direct array)
        setFiles(response.data);
      } else {
        console.error("Unexpected response format:", response.data);
        throw new Error("Invalid response format when loading files");
      }
    } catch (err) {
      console.error("Error loading files:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load files. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setError(null);
      return;
    }
    fetchFiles();
  }, [isOpen, context, fetchFiles]);

  const toggleFileSelection = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleAttach = () => {
    try {
      const selected = files.filter((file) => selectedFiles.includes(file.id));
      if (selected.length === 0) {
        setError("Please select at least one file to attach");
        return;
      }
      onAttach(selected);
      onClose();
    } catch (error) {
      console.error("Error attaching files:", error);
      setError("Failed to attach files. Please try again.");
    }
  };

  const handleFilePreview = (file) => {
    setPreviewFile(file);
    setShowPreview(true);
  };

  const handleUploadSuccess = async (uploadedFiles) => {
    try {
      setUploading(true);

      // Ensure we have an array of files to process
      let filesToAdd = [];

      if (Array.isArray(uploadedFiles)) {
        filesToAdd = uploadedFiles;
      } else if (uploadedFiles && typeof uploadedFiles === "object") {
        // Handle single file object or response with nested data
        if (uploadedFiles.file) {
          // Handle case where response is { file: {...} }
          filesToAdd = [uploadedFiles.file];
        } else if (uploadedFiles.id) {
          // Handle case where response is the file object directly
          filesToAdd = [uploadedFiles];
        } else if (uploadedFiles.data) {
          // Handle case where response is { data: {...} }
          filesToAdd = uploadedFiles.data.file ? [uploadedFiles.data.file] : [];
        }
      }

      if (filesToAdd.length > 0) {
        // Add new files to the beginning of the list
        setFiles((prev) => [
          ...filesToAdd.filter((f) => f && typeof f === "object"),
          ...prev.filter((f) => f && typeof f === "object"),
        ]);

        // If this is a TPN context, automatically select the new files
        if (context.type === "user_tpn") {
          const newFileIds = filesToAdd
            .map((file) => file?.id)
            .filter(Boolean);
          if (newFileIds.length > 0) {
            setSelectedFiles((prev) => [...new Set([...prev, ...newFileIds])]);
          }
        }

        // Refresh from server to ensure we show the latest list/state
        await fetchFiles();
      } else {
        console.warn("No valid files found in upload response:", uploadedFiles);
        setError("Uploaded files could not be processed. Please try again.");
      }
    } catch (error) {
      console.error("Error handling upload:", error);
      setError("Failed to process uploaded files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="h-6 w-6"
                    aria-hidden="true"
                  />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    Select Files to Attach
                  </Dialog.Title>

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md">
                      {error}
                    </div>
                  )}

                  {/* File Upload Section */}
                  <div className="mt-4">
                    <FileUpload
                      context={context}
                      current_booking={context?.current_booking}
                      onUploadSuccess={handleUploadSuccess}
                    />
                  </div>

                  {/* Search Input */}
                  <div className="mt-4">
                    <input
                      type="text"
                      placeholder="Search files by name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Files List */}
                  <div className="mt-4 border-t border-gray-200">
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className="animate-spin text-primary text-xl"
                        />
                        <span className="ml-2">Loading files...</span>
                      </div>
                    ) : filteredFiles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm 
                          ? "No files match your search." 
                          : `No files found for this ${context.type === "user_tpn" ? "TPN" : "booking"}`}
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {filteredFiles.filter(Boolean).map((file) => (
                          <li key={file.id} className="py-3 flex items-center">
                            <div className="flex items-center flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={selectedFiles.includes(file.id)}
                                onChange={() => toggleFileSelection(file.id)}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mr-3"
                              />
                              <span
                                className="text-sm font-medium text-gray-900 truncate flex-1 cursor-pointer"
                                onClick={() => handleFilePreview(file)}
                              >
                                {file.name || file.original_name || (file.id ? `File #${file.id}` : "(unnamed file)")}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleFilePreview(file)}
                                className="ml-2 text-sm text-primary hover:text-primary_hover"
                              >
                                Preview
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={handleAttach}
                  disabled={selectedFiles.length === 0 || uploading}
                >
                  <FontAwesomeIcon icon={faCheck} className="mr-2" />
                  {uploading
                    ? "Processing..."
                    : `Attach ${selectedFiles.length} ${
                        selectedFiles.length === 1 ? "File" : "Files"
                      }`}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onClose}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>

        {/* File Preview Modal */}
        {showPreview && previewFile && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              >
                <div
                  className="absolute inset-0 bg-gray-500 opacity-75"
                  onClick={() => setShowPreview(false)}
                ></div>
              </div>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => setShowPreview(false)}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="mt-4">
                  <FileDisplay file={previewFile} onScreen={true} />
                  {previewFile.type && previewFile.type.includes("pdf") && (
                    <ViewFile file={previewFile} />
                  )}
                  {previewFile.type && previewFile.type.includes("image") && (
                    <img
                      src={`${process.env.REACT_APP_R2_DEVURL}/public/${previewFile.path}`}
                      alt={previewFile.name}
                      className="max-w-full h-auto mt-4 rounded"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </Transition.Root>
  );
}
